import React, { useState, useEffect, useMemo } from 'react';

import Header from "../Header";

import FindCustomer from "./FindCustomer";

import useDeviceProfile from '../../Utils/UseDeviceProfile';

import { mapServiceUrls, getDisplayValue, mapUrlToOldSnippet, copyTextToClipboard, sanitizeCustomerName } from '../../Helpers/Globals';

import useServiceUrls from '../../Utils/UseServiceUrls';

import { getQueryString, getReturnUrl, readCartSnippetParams, handleCartAlerts, onSnippetComplete, buildAlertResumeRouteUrl, completeAlertChain, clearAlertResumeGuard } from '../../Helpers/Globals';

import { Button, Snackbar } from '@mui/material';

import useSetCartCustomer from '../../Utils/UseSetCartCustomer';

import { client } from '../../Utils/ApiClient';



// View Customer snippet reads returnURL, not bl_rurl.

const getCaseSensitiveReturnUrl = () => {

    const raw = getReturnUrl(window.location.href);

    if (!raw) return '';

    try {

        return decodeURIComponent(raw);

    } catch {

        return raw;

    }

};



const remapBlRurlQueryToReturnURL = (href, returnUrlValue) => {

    try {

        const url = new URL(href);

        const next = new URLSearchParams();

        for (const [key, value] of url.searchParams.entries()) {

            if (key.toLowerCase() === 'bl_rurl') continue;

            next.append(key, value);

        }

        if (returnUrlValue) {

            next.set('returnURL', returnUrlValue);

        }

        url.search = next.toString();

        return url.toString();

    } catch {

        return href;

    }

};



export default function FindCustomerSnippet(props) {

    const { hideHeader = false, onCustomerSelected } = props;

    const initialParams = useMemo(() => readCartSnippetParams(), []);

    const [sessionKey, setSessionKey] = useState(initialParams.sessionKey);

    const [loginDomain, setLoginDomain] = useState(initialParams.loginDomain);

    const [cartUid, setCartUid] = useState(initialParams.cartUid);

    const [cartExternalId, setCartExternalId] = useState(initialParams.cartExternalId);

    const [cartSourceExternalId, setCartSourceExternalId] = useState(initialParams.cartSourceExternalId);

    const [returnUrl, setReturnUrl] = useState(initialParams.returnUrl)

    const [selectedCustomerUID, setSelectedCustomerUID] = useState('');

    const [selectedCustomer, setSelectedCustomer] = useState(null)

    const { serviceUrlsStatus, serviceUrls, serviceUrlsError } = useServiceUrls(loginDomain);

    const {status, deviceProfile, error} = useDeviceProfile(mapServiceUrls(serviceUrls), sessionKey);

    const [copyNoticeOpen, setCopyNoticeOpen] = useState(false)



    const setCartCustomer = useSetCartCustomer()



    const hasCart = useMemo(() => {

        return (cartUid && cartUid.length > 0) || (cartExternalId && cartExternalId.length > 0) || (cartSourceExternalId && cartSourceExternalId.length > 0)

    }, [cartUid, cartExternalId, cartSourceExternalId])



    useEffect(() => {

        const params = readCartSnippetParams()

        setSessionKey(params.sessionKey);

        setLoginDomain(params.loginDomain);

        setReturnUrl(params.returnUrl)

        setCartUid(params.cartUid);

        setCartExternalId(params.cartExternalId)

        setCartSourceExternalId(params.cartSourceExternalId)

    }, [window.location.search])



    const fetchCartAndHandleAlerts = async () => {

        if (!serviceUrls || !sessionKey || !hasCart || onCustomerSelected) {

            return

        }



        const urls = mapServiceUrls(serviceUrls)

        const uniqueParam = new Date().getTime()

        const cartQuery = `${cartUid ? `cartUid=${cartUid}&` : ''}${cartExternalId ? `externalId=${cartExternalId}&` : ''}${cartSourceExternalId ? `sourceExternalId=${cartSourceExternalId}&` : ''}`



        try {

            console.log('[FindCustomer] fetching cart for alerts', { sessionKey, cartUid })

            const response = await client(urls.LoyaltyEngineApiUrl, `api/v4/${sessionKey}/carts?${cartQuery}_=${uniqueParam}`)

            const cartData = response?.data

            console.log('[FindCustomer] cart fetch complete', {

                alertCount: cartData?.LoyaltySummary?.Alerts?.length ?? 0,

                alerts: cartData?.LoyaltySummary?.Alerts,

            })

            handleAlerts(cartData)

        } catch (error) {

            console.log('[FindCustomer] cart fetch failed', error)

        }

    }



    useEffect(() => {

        if (setCartCustomer.status === 'success') {

            snippetDone()

        }

    }, [setCartCustomer.status])



    const snippetDone = async (customer) => {

        if (onCustomerSelected) {

            if (customer || selectedCustomer) {

                const customerToCopy = customer || selectedCustomer

                const sanitizedFirstName = sanitizeCustomerName(customerToCopy.FirstName)

                const sanitizedLastName = sanitizeCustomerName(customerToCopy.LastName)

                await copyTextToClipboard(`${customerToCopy.Code} | ${sanitizedFirstName} ${sanitizedLastName}`)

                setCopyNoticeOpen(true)

                onCustomerSelected(customerToCopy)

            }

            return

        }



        if (hasCart) {

            await fetchCartAndHandleAlerts()

            return

        }



        if (customer) {

            const sanitizedFirstName = sanitizeCustomerName(customer.FirstName)

            const sanitizedLastName = sanitizeCustomerName(customer.LastName)

            await copyTextToClipboard(`${customer.Code} | ${sanitizedFirstName} ${sanitizedLastName}`)

            setCopyNoticeOpen(true)

            setTimeout(() => {

                onSnippetComplete(returnUrl)

            }, 1200)

            return

        }



        onSnippetComplete(returnUrl)

    }



    const updateSelectedCustomer = (customer) => {

        setSelectedCustomerUID(customer.Uid)

        setSelectedCustomer(customer)

    }



    const setCustomerToCart = (event) => {

        event.preventDefault();

        if (hasCart)

            setCartCustomer.mutate({urls: serviceUrls, cartUid, cartExternalId, cartSourceExternalId, customer: selectedCustomer, accessKey: sessionKey})

        else

            snippetDone()

    }



    const handleFindCustomerOption = (option) => {

        if (props.onFindCustomerOption) {

            props.onFindCustomerOption(option, selectedCustomerUID, selectedCustomer)

            return

        }



        switch (option) {

            case 'QuickSignup':

                window.location.href = `/POS/QuickSignup${window.location.search}`;

                break;

            case 'ViewCustomer':

                let url = mapUrlToOldSnippet(window.location.href)

                if (url.toLowerCase().includes('findcustomer')) {

                    url = url.replace(/findcustomer/i, 'viewcustomer');

                }

                url = remapBlRurlQueryToReturnURL(url, getCaseSensitiveReturnUrl());

                const newUrl = `${url}&SelectedCustomerUid=${selectedCustomerUID}`;

                window.location.href = newUrl;

                break;

            case 'NewCustomer':

                window.location.href = `/POS/QuickSignup${window.location.search}`;

                break;

            default:

                console.log('Unknown option:', option);

        }

    }



    const handleAlerts = (cartResponse) => {

        const resolvedReturnUrl = returnUrl || getReturnUrl(window.location.href)

        clearAlertResumeGuard(readCartSnippetParams())

        handleCartAlerts(

            cartResponse,

            resolvedReturnUrl,

            () => completeAlertChain(resolvedReturnUrl),

            { resumeUrl: buildAlertResumeRouteUrl(window.location.href, window.location.href) }

        )

    }



    return (

        <div className={'container'} style={{

            maxWidth: '100%',

            width: '100%'

        }}>

            {!hideHeader && <Header title={'Find Customer'}/>}

            <div className={'findCustomerSnippetContainer center'} style={{

            }}>

                <FindCustomer {...props} close={snippetDone} onCustomerSelected={updateSelectedCustomer} allowExact={true}/>

                {deviceProfile?.POSSnippetSettings?.FindCustomerOptions && deviceProfile.POSSnippetSettings.FindCustomerOptions.length > 0 && (() => {

                    const filteredOptions = props.onFindCustomerOption 

                        ? deviceProfile.POSSnippetSettings.FindCustomerOptions.map(option => option === 'ViewCustomer' ? 'QuickEdit' : option)

                        : deviceProfile.POSSnippetSettings.FindCustomerOptions;

                    

                    if (filteredOptions.length === 0) return null;

                    

                    return (

                        <>

                            <br />

                            <div style={{textAlign: 'right',}}>

                                <Button

                                    style={{margin: '5px', minWidth: '120px'}}

                                    variant='contained'

                                    color='primary'

                                    disabled={selectedCustomerUID === ''}

                                    onClick={setCustomerToCart}

                                >

                                    Set

                                </Button>

                                {!onCustomerSelected && (

                                    <Button

                                        style={{margin: '5px', minWidth: '120px'}}

                                        variant='outlined'

                                        color='primary'

                                        onClick={() => snippetDone()}

                                    >

                                        Cancel

                                    </Button>

                                )}

                                {filteredOptions.map((option, index) => (

                                    <Button

                                        key={index}

                                        style={{margin: '5px', minWidth: '120px'}}

                                        variant='outlined'

                                        color='primary'

                                        disabled={option === 'QuickEdit' && selectedCustomerUID === ''}

                                        onClick={() => handleFindCustomerOption(option)}

                                    >

                                        {getDisplayValue(option)}

                                    </Button>

                                ))}

                            </div>

                        </>

                    );

                })()}

            </div>

            <Snackbar

                open={copyNoticeOpen}

                autoHideDuration={1200}

                onClose={() => setCopyNoticeOpen(false)}

                message={'Customer account number copied to clipboard'}

                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}

            />

        </div>

    );

}


